import type { Bilingual } from '../bilingual.js';

/** Copy da tabela de planos negociados, independente da tabela de catálogo público. */
export interface TargetedPlanTableContent {
  readonly statusExpired: string;
  readonly statusActive: string;
  readonly targetOrg: string;
  readonly targetUser: string;
  readonly targetNone: string;
  readonly columnPlan: string;
  readonly columnTier: string;
  readonly columnTarget: string;
  readonly columnPrice: string;
  readonly columnValidity: string;
  readonly columnStatus: string;
  readonly emptyFilteredTitle: string;
  readonly emptyFilteredDescription: string;
  readonly emptyTitle: string;
  readonly selectPageAriaLabel: string;
  readonly selectRowAriaLabel: (planName: string) => string;
  readonly edit: string;
  readonly editAriaLabel: (planName: string) => string;
  readonly archiveAriaLabel: (planName: string) => string;
}

/** Copy da aba negociada: filtros, arquivamento individual e lote homogêneo. */
export interface TargetedPlanSectionContent {
  readonly intro: string;
  readonly searchAriaLabel: string;
  readonly targetFilterAriaLabel: string;
  readonly targetOrg: string;
  readonly targetUser: string;
  readonly loadError: string;
  readonly archivedToast: string;
  readonly archiveDialogTitle: string;
  readonly archiveDialogDescription: (lookupKey: string) => string;
  readonly bulkSelected: (count: number) => string;
  readonly bulkArchive: string;
  readonly bulkClear: string;
  readonly bulkTitle: string;
  readonly bulkDescription: (count: number) => string;
  readonly bulkConfirm: string;
  readonly bulkSuccess: (count: number) => string;
  readonly bulkPartial: (success: number, failed: number) => string;
}

/** Fatia coesa preservada em `PlansContent` como `targetedTable` + `targetedSection`. */
export interface TargetedPlansContent {
  readonly targetedTable: TargetedPlanTableContent;
  readonly targetedSection: TargetedPlanSectionContent;
}

export const TARGETED_PLANS_CONTENT: Bilingual<TargetedPlansContent> = {
  'pt-BR': {
    targetedTable: {
      statusExpired: 'Expirado',
      statusActive: 'Ativo',
      targetOrg: 'Org',
      targetUser: 'Usuário',
      targetNone: '—',
      columnPlan: 'Plano',
      columnTier: 'Tier',
      columnTarget: 'Alvo',
      columnPrice: 'Preço',
      columnValidity: 'Validade',
      columnStatus: 'Status',
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhum plano negociado corresponde à busca.',
      emptyTitle: 'Sem planos negociados',
      selectPageAriaLabel: 'Selecionar planos negociados visíveis',
      selectRowAriaLabel: (planName) => `Selecionar plano negociado ${planName}`,
      edit: 'Editar',
      editAriaLabel: (planName) => `Editar plano negociado ${planName}`,
      archiveAriaLabel: (planName) => `Arquivar plano negociado ${planName}`,
    },
    targetedSection: {
      intro:
        'Um plano negociado é um plano COMPLETO (preço e capabilities próprios) direcionado a uma ' +
        'organização ou usuário, com validade — não herda nenhum plano público. Só o alvo pode ' +
        'assiná-lo.',
      searchAriaLabel: 'Buscar planos negociados',
      targetFilterAriaLabel: 'Filtrar por alvo',
      targetOrg: 'Organização',
      targetUser: 'Usuário',
      loadError: 'Falha ao carregar planos negociados.',
      archivedToast: 'Plano negociado arquivado.',
      archiveDialogTitle: 'Arquivar plano negociado',
      archiveDialogDescription: (lookupKey) =>
        `Arquiva “${lookupKey}” e o Price no Stripe. O alvo deixa de poder assinar; o licensing ` +
        'volta a resolver o tier público.',
      bulkSelected: (count) => `${count} plano(s) negociado(s) selecionado(s)`,
      bulkArchive: 'Arquivar selecionados',
      bulkClear: 'Limpar seleção',
      bulkTitle: 'Arquivar planos negociados',
      bulkDescription: (count) =>
        `Arquivar ${count} plano(s) negociado(s), em sequência, no catálogo e no Stripe?`,
      bulkConfirm: 'Arquivar planos',
      bulkSuccess: (count) => `${count} plano(s) negociado(s) arquivado(s).`,
      bulkPartial: (success, failed) => `${success} arquivado(s); ${failed} com falha.`,
    },
  },
  'en-US': {
    targetedTable: {
      statusExpired: 'Expired',
      statusActive: 'Active',
      targetOrg: 'Org',
      targetUser: 'User',
      targetNone: '—',
      columnPlan: 'Plan',
      columnTier: 'Tier',
      columnTarget: 'Target',
      columnPrice: 'Price',
      columnValidity: 'Validity',
      columnStatus: 'Status',
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No negotiated plan matches the search.',
      emptyTitle: 'No negotiated plans',
      selectPageAriaLabel: 'Select visible negotiated plans',
      selectRowAriaLabel: (planName) => `Select negotiated plan ${planName}`,
      edit: 'Edit',
      editAriaLabel: (planName) => `Edit negotiated plan ${planName}`,
      archiveAriaLabel: (planName) => `Archive negotiated plan ${planName}`,
    },
    targetedSection: {
      intro:
        'A negotiated plan is a COMPLETE plan (its own price and capabilities) targeted at an ' +
        'organization or user, with a validity window — it does not inherit any public plan. ' +
        'Only the target can subscribe to it.',
      searchAriaLabel: 'Search negotiated plans',
      targetFilterAriaLabel: 'Filter by target',
      targetOrg: 'Organization',
      targetUser: 'User',
      loadError: 'Failed to load negotiated plans.',
      archivedToast: 'Negotiated plan archived.',
      archiveDialogTitle: 'Archive negotiated plan',
      archiveDialogDescription: (lookupKey) =>
        `Archives “${lookupKey}” and its Price in Stripe. The target loses the ability to ` +
        'subscribe; licensing falls back to resolving the public tier.',
      bulkSelected: (count) => `${count} negotiated plan(s) selected`,
      bulkArchive: 'Archive selected',
      bulkClear: 'Clear selection',
      bulkTitle: 'Archive negotiated plans',
      bulkDescription: (count) =>
        `Archive ${count} negotiated plan(s), sequentially, in the catalog and Stripe?`,
      bulkConfirm: 'Archive plans',
      bulkSuccess: (count) => `${count} negotiated plan(s) archived.`,
      bulkPartial: (success, failed) => `${success} archived; ${failed} failed.`,
    },
  },
};
